import axios from 'axios';
import { postCookieRefresh } from './authRefresh';
import { notifyAuthSessionExpired } from 'shared/utils/authSessionEvents';
import { resolveRegistryTenantId } from './resolveRegistryTenantId';
import { resolveApiBaseUrl } from './apiOrigin';
import { notifyIdleActivity } from 'shared/utils/idleSessionManager';

/** API URL resolved per request (supports runtime-config.js + nginx /api/). */
const axiosInstance = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Works with plain objects and Axios 1.x AxiosHeaders (per-request headers must not be overwritten). */
function readHeader(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === 'function') {
    const v = headers.get(name);
    return v != null && String(v).trim() !== '' ? String(v).trim() : null;
  }
  const lower = name.toLowerCase();
  const keys = Object.keys(headers);
  for (let i = 0; i < keys.length; i += 1) {
    if (keys[i].toLowerCase() === lower) {
      const v = headers[keys[i]];
      return v != null && String(v).trim() !== '' ? String(v).trim() : null;
    }
  }
  return null;
}

function removeTenantHeader(headers) {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete('X-TenantID');
    return;
  }
  delete headers['X-TenantID'];
}

function isCentralAuthRequest(url) {
  const u = String(url || '');
  return (
    u.includes('/auth/authenticate') ||
    u.includes('/auth/context') ||
    u.includes('/auth/refresh') ||
    u.includes('/auth/sign-out') ||
    u.includes('/auth/session-policy') ||
    u.includes('/auth/activity') ||
    u.includes('/auth/session-restored')
  );
}

function getCurrentUiRoute() {
  if (typeof window === 'undefined' || !window.location) return null;
  const { pathname, search, hash } = window.location;
  return `${pathname || ''}${search || ''}${hash || ''}` || null;
}

axiosInstance.interceptors.request.use(
  (config) => {
    config.baseURL = resolveApiBaseUrl();
    config.headers = config.headers || {};
    /**
     * Backend {@code TenantContextFilter}: most /api/v1/* need X-TenantID.
     * Central auth (/auth/*) uses the central DB — on / or /login without a tenant in the URL,
     * do not send a stale tenant from sessionStorage or REACT_APP_TENANT_ID.
     */
    const existing = readHeader(config.headers, 'X-TenantID');
    const hasExplicit = existing != null && existing !== '';
    const authRequest = isCentralAuthRequest(config.url);
    if (!hasExplicit) {
      const tenantId = authRequest
        ? resolveRegistryTenantId({ routeOnly: true })
        : resolveRegistryTenantId();
      if (tenantId) {
        config.headers['X-TenantID'] = tenantId;
      } else if (authRequest) {
        removeTenantHeader(config.headers);
      }
    }
    // Default JSON Content-Type breaks multipart uploads — let the browser set boundary for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    if (!readHeader(config.headers, 'X-UI-Route')) {
      const uiRoute = getCurrentUiRoute();
      if (uiRoute) {
        config.headers['X-UI-Route'] = uiRoute;
      }
    }
    if (!authRequest) {
      notifyIdleActivity('api-request');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (r) => {
    const url = String(r?.config?.url || '');
    if (
      !url.includes('/auth/authenticate') &&
      !url.includes('/auth/refresh') &&
      !url.includes('/auth/sign-out') &&
      !url.includes('/auth/session-policy') &&
      !url.includes('/auth/activity')
    ) {
      notifyIdleActivity('api-response');
    }
    return r;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    // Only 401 means the access token may be expired. 403 is authorization and must not log the user out.
    const isAuthFailure = status === 401;
    if (!originalRequest || !isAuthFailure || originalRequest._retry) {
      if (!error.response && error.message === 'Network Error') {
        const base = originalRequest?.baseURL || resolveApiBaseUrl();
        const path = originalRequest?.url || '';
        const target = `${base}${path}`;
        const host =
          typeof window !== 'undefined' ? window.location.host : '';
        error.message = host.includes('localhost')
          ? `Cannot reach the API server (${target}). Start Spring Boot on port 8067 and restart the React dev server (proxy).`
          : `Cannot reach the API (${target}). On the VPS: ensure Spring Boot listens on 8067, nginx proxies /api/, and frontend build uses /api/v1 (not localhost).`;
      }
      return Promise.reject(error);
    }
    const url = String(originalRequest.url || '');
    if (
      url.includes('/auth/authenticate') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/sign-out') ||
      url.includes('/auth/context') ||
      url.includes('/files/image/')
    ) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;
    try {
      await postCookieRefresh();
      return axiosInstance(originalRequest);
    } catch (e) {
      notifyAuthSessionExpired();
      return Promise.reject(e);
    }
  }
);

export default axiosInstance;
